import { useEffect, useState, useCallback } from "react";
import { Users, DollarSign, TrendingUp, Eye, Percent, Clock, ShoppingBag, ArrowUp, ArrowDown, Ban, X, Trash2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import { toast } from "sonner";
import { adminApi, apiError } from "@/lib/adminApi";
import { fmt, Card, Spinner } from "@/components/admin/shared";

const PIE = ["#F4A97F", "#FBC4A8", "#E8B796", "#8A7F86", "#D8A98C", "#B07A4E", "#EFD3BF"];
const RANGES = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This week"], ["last_7", "Last 7 days"],
  ["this_month", "This month"], ["last_month", "Last month"], ["last_30", "Last 30 days"],
  ["this_year", "This year"], ["all_time", "All time"],
];

const fmtDur = (s) => { s = Math.round(s || 0); const m = Math.floor(s / 60); return m > 0 ? `${m}m ${s % 60}s` : `${s}s`; };

const Delta = ({ cur, prev, invert, money }) => {
  if (prev == null) return null;
  if (prev === 0 && cur === 0) return <span className="text-[0.7rem] text-[#B6A99F]">—</span>;
  const change = prev === 0 ? 100 : ((cur - prev) / prev) * 100;
  const up = change >= 0;
  const good = invert ? !up : up;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[0.7rem] ${good ? "text-[#3B8C6E]" : "text-[#C0392B]"}`}>
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{Math.abs(change).toFixed(0)}%
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, cur, prev, money }) => (
  <Card className="!p-5">
    <div className="flex items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFE7D6] text-[#FBC4A8]"><Icon className="h-4 w-4" /></span>
      <Delta cur={cur} prev={prev} money={money} />
    </div>
    <p className="mt-3 font-display text-2xl text-[#2C2B2C]">{value}</p>
    <p className="mt-0.5 text-xs uppercase tracking-wider text-[#8A7F86]">{label}</p>
  </Card>
);

const RatePill = ({ label, value }) => (
  <div className="rounded-2xl border border-[#EDE4D4] bg-[#FFFCFA] p-4 text-center" data-testid={`rate-${label}`}>
    <p className="font-display text-3xl text-[#2C2B2C]">{value}%</p>
    <p className="mt-1 text-[0.68rem] uppercase tracking-wider text-[#8A7F86]">{label}</p>
  </div>
);

function ConversionPath({ funnel }) {
  const steps = [
    ["Visitors", funnel.visitors], ["Added to cart", funnel.add_to_cart],
    ["Reached checkout", funnel.checkout], ["Purchased", funnel.purchases],
  ];
  const max = funnel.visitors || 1;
  return (
    <div className="space-y-3" data-testid="conversion-path">
      {steps.map(([label, val], i) => {
        const prev = i === 0 ? val : steps[i - 1][1];
        const drop = prev > 0 ? Math.round((1 - val / prev) * 100) : 0;
        return (
          <div key={label} data-testid={`funnel-step-${i}`}>
            <div className="flex justify-between text-xs"><span className="text-[#6E6168]">{label}</span>
              <span className="text-[#2C2B2C]"><span className="font-semibold">{val}</span>{i > 0 && <span className="ml-2 text-[#B07A4E]">-{drop}%</span>}</span></div>
            <div className="mt-1.5 h-4 overflow-hidden rounded-full bg-[#F2EAE3]">
              <div className="h-full rounded-full bg-gradient-to-r from-[#F4A97F] to-[#FBC4A8] transition-all" style={{ width: `${(val / max) * 100}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AnalyticsPanel() {
  const [range, setRange] = useState("all_time");
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await adminApi.get(`/admin/analytics?range=${range}&compare=${compare}`);
    setData(data);
    setLoading(false);
  }, [range, compare]);
  useEffect(() => { load(); }, [load]);

  const excludeMyIp = async () => {
    try { const { data: r } = await adminApi.post("/admin/analytics/exclude-ip", {}); toast.success("Your IP is now excluded from analytics"); setData((d) => ({ ...d, excluded_ips: r.excluded_ips })); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
  };
  const removeIp = async (ip) => {
    try { const { data: r } = await adminApi.delete(`/admin/analytics/exclude-ip/${encodeURIComponent(ip)}`); setData((d) => ({ ...d, excluded_ips: r.excluded_ips })); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
  };
  const resetAnalytics = async () => {
    if (!window.confirm("Reset all analytics data? This permanently deletes all tracked visits, pageviews and funnel events. Orders are not affected.")) return;
    try { const { data: r } = await adminApi.post("/admin/analytics/reset"); toast.success(`Analytics reset — ${r.events_deleted} events cleared`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Reset failed"); }
  };

  if (loading && !data) return <Spinner />;
  const t = data.totals;
  const c = data.comparison;

  return (
    <div data-testid="analytics-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Analytics</h1>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[#6E6168]">
            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} data-testid="analytics-compare" /> Compare to previous
          </label>
          <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="analytics-range"
            className="rounded-full border border-[#DACDB4] bg-white px-4 py-2 text-sm text-[#2C2B2C] focus:border-[#FBC4A8] focus:outline-none">
            {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={resetAnalytics} data-testid="analytics-reset"
            className="flex items-center gap-1.5 rounded-full border border-[#E3B4AC] bg-white px-4 py-2 text-[0.62rem] uppercase tracking-wider text-[#C0392B] hover:bg-[#FBEEEC]">
            <Trash2 className="h-3.5 w-3.5" /> Reset data
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi icon={Users} label="Visitors" value={t.visitors} cur={t.visitors} prev={c?.visitors} />
        <Kpi icon={Eye} label="Pageviews" value={t.pageviews} cur={t.pageviews} prev={c?.pageviews} />
        <Kpi icon={ShoppingBag} label="Orders" value={t.purchases} cur={t.purchases} prev={c?.purchases} />
        <Kpi icon={DollarSign} label="Revenue" value={fmt(t.revenue)} cur={t.revenue} prev={c?.revenue} money />
        <Kpi icon={Percent} label="Conversion" value={`${t.conversion_rate}%`} cur={t.conversion_rate} prev={c?.conversion_rate} />
        <Kpi icon={Clock} label="Avg session" value={fmtDur(t.avg_session_seconds)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Conversion path</h3>
          <p className="mt-0.5 mb-4 text-xs text-[#8A7F86]">Drop-off at each step of the journey.</p>
          <ConversionPath funnel={data.funnel} />
        </Card>
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Conversion rates</h3>
          <p className="mt-0.5 mb-4 text-xs text-[#8A7F86]">Share of visitors reaching each step.</p>
          <div className="grid grid-cols-3 gap-3" data-testid="conversion-rates">
            <RatePill label="Add to cart" value={data.rates.atc_rate} />
            <RatePill label="Checkout" value={data.rates.checkout_rate} />
            <RatePill label="Purchase" value={data.rates.purchase_rate} />
          </div>
        </Card>
      </div>

      <Card>
        <h3 className="font-display text-lg text-[#2C2B2C]">Month-on-month traffic</h3>
        <p className="mt-0.5 mb-4 text-xs text-[#8A7F86]">Visitors, add-to-carts, checkouts and orders since December 2025.</p>
        <div className="h-72" data-testid="monthly-traffic-chart">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.monthly} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F2EAE3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8A7F86" }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#8A7F86" }} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#8A7F86" }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="l" dataKey="visitors" name="Visitors" fill="#FBC4A8" radius={[4, 4, 0, 0]} maxBarSize={46} />
              <Line yAxisId="r" type="monotone" dataKey="add_to_cart" name="Add to cart" stroke="#F4A97F" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="checkout" name="Checkout" stroke="#C98A6B" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="r" type="monotone" dataKey="purchases" name="Orders" stroke="#232222" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card>
        <h3 className="font-display text-lg text-[#2C2B2C]">Traffic &amp; revenue over time</h3>
        <div className="mt-4 h-64" data-testid="traffic-revenue-chart">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={data.timeseries} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FBC4A8" stopOpacity={0.5} /><stop offset="100%" stopColor="#FBC4A8" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F2EAE3" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A7F86" }} minTickGap={28} tickFormatter={(v) => (v || "").slice(5)} />
              <YAxis yAxisId="v" tick={{ fontSize: 11, fill: "#8A7F86" }} allowDecimals={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#8A7F86" }} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
              <Tooltip formatter={(val, name) => (name === "Revenue" ? fmt(val) : val)} labelFormatter={(l) => l} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="v" type="monotone" dataKey="visitors" stroke="#F4A97F" strokeWidth={2} fill="url(#v)" name="Visitors" />
              <Line yAxisId="r" type="monotone" dataKey="revenue" stroke="#232222" strokeWidth={2} dot={false} name="Revenue" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Time of day</h3>
          <div className="mt-4 h-52" data-testid="time-of-day">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.time_of_day} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
                <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#8A7F86" }} interval={2} />
                <YAxis tick={{ fontSize: 11, fill: "#8A7F86" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="visits" fill="#FBC4A8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Day of week</h3>
          <div className="mt-4 h-52" data-testid="day-of-week">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.day_of_week} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8A7F86" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8A7F86" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="visits" fill="#F4A97F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Devices</h3>
          <div className="mt-2 h-52">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.devices} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {data.devices.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {data.devices.map((dv, i) => <div key={i} className="flex justify-between text-sm"><span className="flex items-center gap-2 capitalize text-[#6E6168]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />{dv.name}</span><span className="font-semibold text-[#2C2B2C]">{dv.value}</span></div>)}
          </div>
        </Card>
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Top sources</h3>
          <div className="mt-4 space-y-2.5">
            {data.sources.length === 0 ? <p className="text-sm text-[#B6A99F]">No data yet.</p> : data.sources.slice(0, 7).map((s, i) => <div key={i} className="flex justify-between text-sm"><span className="truncate capitalize text-[#6E6168]">{s.name}</span><span className="font-semibold text-[#2C2B2C]">{s.value}</span></div>)}
          </div>
        </Card>
        <Card>
          <h3 className="font-display text-lg text-[#2C2B2C]">Top pages</h3>
          <div className="mt-4 space-y-2.5">
            {data.top_pages.length === 0 ? <p className="text-sm text-[#B6A99F]">No data yet.</p> : data.top_pages.map((p, i) => <div key={i} className="flex justify-between text-sm"><span className="truncate text-[#6E6168]">{p.name}</span><span className="font-semibold text-[#2C2B2C]">{p.value}</span></div>)}
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg text-[#2C2B2C]"><Ban className="h-4 w-4 text-[#FBC4A8]" /> Exclude your own traffic</h3>
            <p className="mt-0.5 text-xs text-[#8A7F86]">Keep your own visits &amp; test sessions out of the numbers.</p>
          </div>
          <button onClick={excludeMyIp} data-testid="exclude-my-ip" className="rounded-full bg-[#232222] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3]">Exclude my IP</button>
        </div>
        {(data.excluded_ips || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="excluded-ips">
            {data.excluded_ips.map((ip) => (
              <span key={ip} className="inline-flex items-center gap-1.5 rounded-full bg-[#F2E8E6] px-3 py-1 font-mono text-xs text-[#2C2B2C]">{ip}<button onClick={() => removeIp(ip)} data-testid={`remove-ip-${ip}`}><X className="h-3 w-3" /></button></span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
